// Google Analytics 4 utility functions

import { secureLog } from '@/utils/secureLogger';

// Type definitions
interface GAEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
}

declare global {
  interface Window {
    gtag?: (
      command: 'config' | 'event' | 'js',
      targetId: string | Date,
      config?: Record<string, any>
    ) => void;
    dataLayer?: any[];
  }
}

// Initialize Google Analytics
export const initGA = (measurementId: string) => {
  // Create gtag script
  const script1 = document.createElement('script');
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script1);

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer?.push(arguments);
  };
  
  if (window.gtag) {
    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      page_path: window.location.pathname,
    });
  }

  secureLog.info('Google Analytics initialized');
};

// Track page views
export const trackPageView = (path: string, title?: string) => {
  if (window.gtag) {
    window.gtag('config', 'G-Q0HJ5DB6WL', {
      page_path: path,
      page_title: title || document.title,
    });
    secureLog.info('Page view tracked:', { path });
  }
};

// Track custom events
export const trackEvent = ({ action, category, label, value }: GAEvent) => {
  if (window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
    secureLog.info('Event tracked:', { action, category });
  }
};

// Track promo views
export const trackPromoView = (promoName: string) => {
  trackEvent({
    action: 'view_promo',
    category: 'Engagement',
    label: promoName,
  });
};

// Track promo clicks
export const trackPromoClick = (promoName: string, type: 'website' | 'amazon' | 'featured_amazon' | 'regolamento') => {
  trackEvent({
    action: 'click_promo_link',
    category: 'Engagement',
    label: `${promoName} - ${type}`,
  });
};

// Track province selection
export const trackProvinceSelect = (province: string) => {
  trackEvent({
    action: 'select_province',
    category: 'User_Action',
    label: province,
  });
};

// Track search
export const trackSearch = (searchTerm: string) => {
  trackEvent({
    action: 'search',
    category: 'User_Action',
    label: searchTerm,
  });
};

// Track ad impressions
export const trackAdImpression = (adType: 'banner' | 'interstitial') => {
  trackEvent({
    action: 'ad_impression',
    category: 'Advertisement',
    label: adType,
  });
};

// Track Amazon product clicks
export const trackAmazonClick = (productName: string, source: 'featured' | 'promo_detail') => {
  trackEvent({
    action: 'amazon_product_click',
    category: 'Conversion',
    label: `${productName} - ${source}`,
  });
};

// Track share actions
export const trackShare = (promoName: string, method: 'clipboard' | 'native') => {
  trackEvent({
    action: 'share_promo',
    category: 'Engagement',
    label: `${promoName} - ${method}`,
  });
};

// Track affiliate link clicks
export const trackAffiliateClick = (promoName: string, affiliateName: string) => {
  trackEvent({
    action: 'affiliate_link_click',
    category: 'Conversion',
    label: `${promoName} - ${affiliateName}`,
  });
};

// Track external site visits
export const trackExternalSiteVisit = (promoName: string, url: string) => {
  trackEvent({
    action: 'external_site_visit',
    category: 'Conversion',
    label: `${promoName} - ${new URL(url).hostname}`,
  });
};

// Track regulation views
export const trackRegulationView = (promoName: string) => {
  trackEvent({
    action: 'view_regulation',
    category: 'Engagement',
    label: promoName,
  });
};

// Track contact interactions
export const trackContactClick = (promoName: string, contactType: 'email' | 'phone') => {
  trackEvent({
    action: 'contact_click',
    category: 'Engagement',
    label: `${promoName} - ${contactType}`,
  });
};

// Cookie consent preference types
interface CookieConsentPreferences {
  necessary: boolean;
  functionality: boolean;
  experience: boolean;
  measurement: boolean;
  marketing: boolean;
}

// Track cookie consent changes
export const trackCookieConsent = (
  consentType: 'accepted' | 'rejected' | 'custom',
  preferences: CookieConsentPreferences,
  isUpdate: boolean = false
) => {
  // Build enabled categories list
  const enabledCategories = Object.entries(preferences)
    .filter(([_, enabled]) => enabled)
    .map(([category]) => category)
    .join(',');

  const action = isUpdate ? 'cookie_consent_updated' : 'cookie_consent_given';

  trackEvent({
    action,
    category: 'Privacy',
    label: `${consentType}: ${enabledCategories}`,
  });

  // Log detailed consent for GDPR audit trail
  secureLog.info('Cookie consent recorded:', {
    consentType,
    preferences,
    isUpdate,
    timestamp: new Date().toISOString(),
  });
};

// Track cookie banner interactions
export const trackCookieBannerInteraction = (
  interaction: 'shown' | 'preferences_opened' | 'preferences_closed' | 'widget_clicked'
) => {
  trackEvent({
    action: 'cookie_banner_interaction',
    category: 'Privacy',
    label: interaction,
  });
};
